/**
 * Focus the search-modal input on mobile so the on-screen keyboard pops up
 * immediately when the modal opens.
 *
 * iOS only opens the keyboard for an input.focus() call that happens
 * synchronously inside a user-gesture (tap) call stack. dialog.js opens the
 * modal asynchronously via requestAnimationFrame, which breaks that chain.
 *
 * Trick: the moment the user taps the search trigger, we synchronously
 * focus a hidden proxy <input> that lives outside the dialog. iOS pops the
 * keyboard because we focused a real input during the gesture. Then once
 * the dialog actually opens we transfer focus to the real search input —
 * iOS keeps the keyboard up because focus moves between two real inputs.
 */

const SEARCH_MODAL_ID = 'search-modal';
const TRIGGER_SELECTOR = `[on\\:click="#${SEARCH_MODAL_ID}/showDialog"]`;

// Hidden proxy input. font-size:16px prevents iOS zoom; opacity:0 + 1px
// dimensions keep it invisible. Crucially it's positioned within the
// viewport (not offscreen) so iOS treats it as focusable.
const proxyInput = document.createElement('input');
proxyInput.type = 'search';
proxyInput.setAttribute('aria-hidden', 'true');
proxyInput.setAttribute('tabindex', '-1');
proxyInput.style.cssText =
  'position:fixed;top:0;left:0;width:1px;height:1px;opacity:0;border:0;background:transparent;font-size:16px;z-index:-1;';
document.body.appendChild(proxyInput);

const focusRealInput = () => {
  const modal = document.getElementById(SEARCH_MODAL_ID);
  if (!modal) return;
  const input = modal.querySelector('input.search-input');
  if (!input) return;
  try {
    input.focus({ preventScroll: true });
  } catch (_e) {
    input.focus();
  }
};

// Capture-phase click handler — runs BEFORE the framework's on:click
// handler that opens the dialog asynchronously.
document.addEventListener(
  'click',
  (event) => {
    const trigger = event.target.closest(TRIGGER_SELECTOR);
    if (!trigger) return;
    // Synchronously focus the proxy input. This primes the iOS keyboard
    // while we're still inside the user's tap event.
    try {
      proxyInput.focus({ preventScroll: true });
    } catch (_e) {
      proxyInput.focus();
    }
  },
  true
);

// Watch for the dialog opening, then transfer focus to the real input.
// iOS keeps the keyboard up across this focus transfer.
const wireUp = () => {
  const modal = document.getElementById(SEARCH_MODAL_ID);
  if (!modal) return;
  const dialog = modal.querySelector('dialog');
  if (!dialog) return;

  new MutationObserver(() => {
    if (dialog.hasAttribute('open')) focusRealInput();
  }).observe(dialog, { attributes: true, attributeFilter: ['open'] });

  // Also listen for the dialog:open custom event (covers other code paths).
  modal.addEventListener('dialog:open', focusRealInput);
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', wireUp);
} else {
  wireUp();
}